import { convexQuery } from "@convex-dev/react-query";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { formatDateUTC } from "@plasma/utils/time";
import { timeConvert24hrTo12hr } from "@plasma/utils/time-convert";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import {
  type ColumnDef,
  createColumnHelper,
  type Row,
  type Table,
} from "@tanstack/react-table";
import { ClockPlus, Trash2 } from "lucide-react";
import { createContext, use, useCallback, useMemo } from "react";
import type { DateRange } from "react-day-picker";

import { LoadingPanel } from "~/components/shared/loading-panel";
import { Button } from "~/components/ui/button";
import { DataTable, DataTableFacetedFilter } from "~/components/ui/data-table";
import { DateRangeInput } from "~/components/ui/date-range-input";
import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

const employeeShiftsGetAll = api.employeeShifts.getAll;

interface EmployeeShift {
  id: Id<"employeeShifts">;
  location: { id: Id<"locations">; name: string; imageUrl?: string };
  employee: { id: Id<"employees">; name: string; imageUrl?: string };
  date: number;
  startTime: string;
  endTime: string;
  minutesWorked: number;
  estimatedWage: number | null;
  isAutoEnded: boolean;
}

const columnHelper = createColumnHelper<EmployeeShift>();

// V4.1: Navigation context for route-based modals
type NavigateToShiftModal = (
  type: "edit" | "delete",
  shift: EmployeeShift
) => void;
const NavigateToShiftModalContext = createContext<NavigateToShiftModal | null>(
  null
);

const useNavigateToShiftModal = () => {
  const context = use(NavigateToShiftModalContext);
  if (!context) {
    throw new Error(
      "useNavigateToShiftModal must be used within a NavigateToShiftModalContext.Provider"
    );
  }
  return context;
};

const ActionsColumnCell = ({ row }: { row: Row<EmployeeShift> }) => {
  const navigateToShiftModal = useNavigateToShiftModal();

  return (
    <div className="flex items-center">
      <Button
        className="body-sm text-weight-medium rounded-lg px-2 py-1"
        onClick={() => navigateToShiftModal("edit", row.original)}
        size="sm"
        type="button"
        variant="ghost"
      >
        Edit
      </Button>
      <Button
        className="p-1.5"
        onClick={() => navigateToShiftModal("delete", row.original)}
        size="icon"
        type="button"
        variant="ghost"
      >
        <Trash2 className="w-full" />
      </Button>
    </div>
  );
};

const columns = [
  columnHelper.accessor("date", {
    header: "Date",
    cell: ({ getValue }) => formatDateUTC(new Date(getValue()), "MMM D, YYYY"),
  }),
  columnHelper.accessor("employee", {
    header: "Employee",
    cell: ({ getValue }) => {
      const employee = getValue();
      return (
        <div className="flex items-center gap-3">
          <img
            alt={`${employee.name}'s profile`}
            className="size-6 rounded-full"
            height={24}
            src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
            width={24}
          />
          <p>{employee.name}</p>
        </div>
      );
    },
    filterFn: ({ original }, _, filterValue: string[]) =>
      filterValue.includes(original.employee.id),
  }),
  columnHelper.accessor("location", {
    header: "Location",
    cell: ({ getValue }) => getValue().name,
    filterFn: ({ original }, _, filterValue: string[]) =>
      filterValue.includes(original.location.id),
  }),
  columnHelper.accessor("startTime", {
    header: "Start Time",
    cell: ({ getValue }) => timeConvert24hrTo12hr(getValue()),
  }),
  columnHelper.accessor("endTime", {
    header: "End Time",
    cell: ({ getValue, row }) => {
      const endTime = timeConvert24hrTo12hr(getValue());
      const isAutoEnded = row.original.isAutoEnded;
      return (
        <div className="flex items-center gap-2">
          <span>{endTime}</span>
          {isAutoEnded && (
            <span className="caption bg-warning/10 text-warning rounded-sm px-3 py-1">
              Auto-ended
            </span>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor("minutesWorked", {
    header: "Total Hours",
    cell: ({ getValue }) => {
      const minutes = getValue();
      return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    },
  }),
  columnHelper.accessor("estimatedWage", {
    header: "Est. Hr. Wages",
    cell: ({ getValue }) =>
      getValue()?.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }) ?? "-",
  }),
  columnHelper.display({
    id: "actions",
    cell: ActionsColumnCell,
  }),
] as ColumnDef<EmployeeShift>[];

export const Route = createFileRoute("/_authenticated/employees/shifts")({
  component: EmployeeShiftsPage,
});

function EmployeeShiftsPage() {
  const navigate = useNavigate();
  const searchParamsDateRange = useSearchParamsDateRange();
  const dateRange = searchParamsDateRange[0];
  const setDateRange = searchParamsDateRange[1];

  const dateRangeParams = useMemo(
    () => ({
      from: dateRange.from?.getTime() ?? 0,
      to: dateRange.to?.getTime() ?? 0,
    }),
    [dateRange]
  );
  const { data: shiftsResult, isPending } = useQuery(
    convexQuery(employeeShiftsGetAll, {
      dateRange: dateRangeParams,
      paginationOpts: { numItems: 500, cursor: null },
    })
  );
  const shifts = shiftsResult?.page;

  // V4.1: Route-based navigation for shift modals
  const navigateToShiftModal: NavigateToShiftModal = useCallback(
    (type, shift) => {
      if (type === "edit") {
        void navigate({
          to: `/employees/shifts/${shift.id}/edit`,
          search: {
            employeeName: shift.employee.name,
            employeeImageUrl: shift.employee.imageUrl ?? undefined,
            locationName: shift.location.name,
            date: String(shift.date),
            startTime: shift.startTime,
            endTime: shift.endTime,
          },
        });
      } else {
        void navigate({
          to: `/employees/shifts/${shift.id}/delete`,
          search: {
            employeeName: shift.employee.name,
            employeeImageUrl: shift.employee.imageUrl ?? undefined,
            date: String(shift.date),
            startTime: shift.startTime,
            endTime: shift.endTime,
            minutesWorked: shift.minutesWorked,
            estimatedWage: shift.estimatedWage ?? undefined,
          },
        });
      }
    },
    [navigate]
  );

  // V4.1: Navigation callback for "Add Shift" button
  const navigateToNewShift = useCallback(() => {
    void navigate({ to: "/employees/shifts/new" });
  }, [navigate]);

  if (isPending || !shifts) {
    return <LoadingPanel message="Loading shifts..." />;
  }

  return (
    <>
      <NavigateToShiftModalContext value={navigateToShiftModal}>
        <NavigateToNewShiftContext value={navigateToNewShift}>
          <DataTable
            columns={columns}
            data={shifts}
            defaultSorting={[
              {
                id: "date",
                desc: false,
              },
            ]}
            id="shiftsTable"
            noBorders
            toolbar={(table) => (
              <Toolbar
                dateRange={dateRange}
                setDateRange={setDateRange}
                table={table}
              />
            )}
            zebraStyle
          />
        </NavigateToNewShiftContext>
      </NavigateToShiftModalContext>
      {/* V4.1: Outlet for child routes (new, edit, delete modals) */}
      <Outlet />
    </>
  );
}

// V4.1: Navigation context for toolbar "Add Shift" button
const NavigateToNewShiftContext = createContext<(() => void) | null>(null);

const useNavigateToNewShift = () => {
  const context = use(NavigateToNewShiftContext);
  if (!context) {
    throw new Error(
      "useNavigateToNewShift must be used within a NavigateToNewShiftContext.Provider"
    );
  }
  return context;
};

const Toolbar = ({
  table,
  dateRange,
  setDateRange,
}: {
  table: Table<EmployeeShift>;
  dateRange: DateRange;
  setDateRange: (start: Date, end: Date) => void;
}) => {
  const navigateToNewShift = useNavigateToNewShift();

  return (
    <div className="flex items-center gap-2" id="tool-bar">
      <DateRangeInput
        className="body-sm h-9 px-3 py-2"
        onValueChange={({ from, to }) => from && to && setDateRange(from, to)}
        value={dateRange}
      />
      <DataTableFacetedFilter
        column={table.getColumn("location")}
        options={Object.values(
          Object.fromEntries(
            table
              .getCoreRowModel()
              .rows.map((r) => ({
                value: r.original.location.id,
                label: r.original.location.name,
              }))
              .map((r) => [r.value, r])
          )
        )}
        placeholder="Search location name..."
        title="Location"
      />
      <DataTableFacetedFilter
        column={table.getColumn("employee")}
        options={Object.values(
          Object.fromEntries(
            table
              .getCoreRowModel()
              .rows.map((r) => ({
                value: r.original.employee.id,
                label: r.original.employee.name,
              }))
              .map((r) => [r.value, r])
          )
        )}
        placeholder="Search employee name..."
        title="Employee"
      />
      <div className="flex-1" />
      <Button
        onClick={navigateToNewShift}
        size="sm"
        type="button"
        variant="outline"
      >
        <ClockPlus className="text-foreground size-4" />
        Add Shift
      </Button>
    </div>
  );
};
